/**
 * subscriptionConfig.js
 * Centralized tier definitions for the 2-tier subscription system.
 * Free / Premium ($49)
 */

export const TIER_CONFIG = {
  free: {
    label: "Free",
    price: 0,
    color: "orange",
    miniCourses: 0,
    fullCourses: 0,
    videos: 0,
    pdfs: 0,
    dashboardTheme: {
      accent: "orange",
      text: "text-orange-500",
      bg: "bg-orange-500",
      bgSubtle: "bg-orange-500/10",
      border: "border-orange-500/30",
      borderHover: "hover:border-orange-500/50",
      gradient: "from-orange-500/10 to-amber-500/10",
      button: "bg-orange-500 hover:bg-orange-400",
      ring: "ring-orange-500/10",
      badge: "bg-orange-500/20 text-orange-500",
    },
  },
  premium: {
    label: "Premium",
    price: 49,
    color: "cyan",
    miniCourses: 5,
    fullCourses: 0,
    videos: 0,
    pdfs: 10,
    dashboardTheme: {
      accent: "cyan",
      text: "text-cyan-500",
      bg: "bg-cyan-500",
      bgSubtle: "bg-cyan-500/10",
      border: "border-cyan-500/30",
      borderHover: "hover:border-cyan-500/50",
      gradient: "from-cyan-500/10 to-teal-500/10",
      button: "bg-cyan-500 hover:bg-cyan-400",
      ring: "ring-cyan-500/10",
      badge: "bg-cyan-500/20 text-cyan-500",
    },
  },
};

// Legacy paid tiers (professional, enterprise) count as Premium so no paid member loses access
export const TIER_HIERARCHY = { free: 0, premium: 1, professional: 1, enterprise: 1 };

/**
 * Returns true if `userTier` is at or above `requiredTier`
 */
export function meetsMinTier(userTier, requiredTier) {
  return (TIER_HIERARCHY[userTier] ?? 0) >= (TIER_HIERARCHY[requiredTier] ?? 0);
}

/**
 * Stripe Price IDs — Premium subscription only.
 */
export const STRIPE_PRICES = {
  premium: "price_1T0mD3BZXvLhG7cUyckVLnWJ",
};

/**
 * AI for Project Managers — Access Config
 *
 * All pricing comes from Firestore (productsAndServices + courses collections).
 * This config only defines which slugs belong to the AI PM bundle
 * and which courses premium members get for free.
 *
 * Firestore fields used for pricing display:
 *   price          — current selling price (what the user pays)
 *   originalPrice  — regular price before any promo (shown as strikethrough)
 *   promoLabel     — optional label like "Founders Price" shown next to discounted price
 */
export const AI_PM_PRICING = {
  bundleSlug: "ai-for-pms",
  bundleProductSlug: "ai-foundations-for-pms",
  stripeProductId: "prod_U6QuMYYqtp1xLF",
  stripePriceId: "price_1T8E5mBZXvLhG7cU9DdF69I6",
  price: 199,
  /** Course slug that is free for all users */
  freeCourseSlug: "ai-foundations",
  /** Kept for backwards compat — no courses are free with premium */
  premiumFreeSlugs: [],
  /** All individual course slugs in the bundle */
  allCourseSlugs: [
    "ai-foundations",
    "ai-planning-estimation",
    "ai-risk-decision-intelligence",
    "ai-stakeholder-communication",
    "ai-agile-hybrid-delivery",
    "ai-reporting-dashboards",
    "ai-quality-change-control",
    "ai-tools-lab",
    "ai-leadership-future",
    "ai-for-pms-capstone",
  ],
};
